import './style.modules.scss'

import React, { useEffect, useRef, useState } from 'react'

import { BREAKPOINTS } from '../../theme/breakpoints'

export type ImageProps = {
  readonly src: string // default image (required)
  readonly sm?: string // image for small screens
  readonly md?: string // image for medium screens
  readonly lg?: string // image for large screens
  readonly alt?: string // optional, defaults to decorative
  readonly className?: string
  readonly testId?: string
  readonly style?: React.CSSProperties
  readonly loading?: 'eager' | 'lazy'
  readonly priority?: boolean // prioritize loading important images
  readonly skeleton?: boolean // whether to show skeleton loader while image is loading
}

function ResponsiveSources({
  sm,
  md,
  lg
}: Pick<ImageProps, 'sm' | 'md' | 'lg'>) {
  return (
    <>
      {lg ? (
        <source
          srcSet={lg}
          media={`(min-width: ${BREAKPOINTS.lg.min}${BREAKPOINTS.lg.uom})`}
        />
      ) : null}
      {md ? (
        <source
          srcSet={md}
          media={`(min-width: ${BREAKPOINTS.md.min}${BREAKPOINTS.md.uom}) and (max-width: ${BREAKPOINTS.md.max}${BREAKPOINTS.md.uom})`}
        />
      ) : null}
      {sm ? (
        <source
          srcSet={sm}
          media={`(max-width: ${BREAKPOINTS.sm.max}${BREAKPOINTS.sm.uom})`}
        />
      ) : null}
    </>
  )
}

export function Image({
  src,
  sm,
  md,
  lg,
  alt,
  className,
  testId,
  style,
  loading = 'lazy', // 'lazy': only loads when near viewport
  skeleton = false,
  priority = false
}: ImageProps) {
  const hasSources = Boolean(sm || md || lg)
  const [isLoading, setIsLoading] = useState(true)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const wrapperClassName = [
    'Image',
    className,
    isLoading && skeleton ? 'is-loading' : ''
  ]
    .filter(Boolean)
    .join(' ')
  const resolvedLoading = priority ? 'eager' : loading
  const resolvedFetchPriority = priority ? 'high' : 'auto'

  const handleLoad = () => {
    setIsLoading(false)
  }

  // Safari and cache fix: call onLoad if already loaded
  useEffect(() => {
    const img = imgRef.current
    if (img && img.complete && isLoading) {
      // Only call if not already handled
      handleLoad()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  return (
    <div
      className={wrapperClassName}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      {hasSources ? (
        <picture data-testid={testId ? `${testId}-picture` : undefined}>
          <ResponsiveSources sm={sm} md={md} lg={lg} />
          <img
            ref={imgRef}
            src={src}
            alt={alt || ''}
            data-testid={testId}
            style={style}
            loading={resolvedLoading}
            onLoad={handleLoad}
            fetchPriority={resolvedFetchPriority}
          />
        </picture>
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          data-testid={testId}
          style={style}
          loading={resolvedLoading}
          onLoad={handleLoad}
          fetchPriority={resolvedFetchPriority}
        />
      )}
    </div>
  )
}
